import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Flame, ImagePlus, Loader2, Trash2 } from "lucide-react";
import { api, errMsg, media, timeAgo } from "@/lib/api";
import { useSeo, PRIVATE_SEO } from "@/lib/seo";

const STATE_LABEL = {
  approved: ["Live", "text-lux-success border-lux-success/40"],
  pending: ["In review", "text-lux-warning border-lux-warning/40"],
  rejected: ["Rejected", "text-lux-error border-lux-error/40"],
  expired: ["Expired", "text-lux-muted border-lux-border"],
  removed: ["Removed", "text-lux-muted border-lux-border"],
};

export default function StoriesPage() {
  useSeo({ title: "Stories | LUX", ...PRIVATE_SEO });
  const client = useQueryClient();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["advertiser", "stories"],
    queryFn: async () => (await api.get("/advertiser/stories")).data,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const upload = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/advertiser/stories", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Story submitted for review");
      await refetch();
      client.invalidateQueries({ queryKey: ["advertiser", "me"] });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/advertiser/stories/${id}`);
      await refetch();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  if (isLoading || !data) {
    return <div className="text-sm text-lux-muted" data-testid="stories-loading">Loading your stories...</div>;
  }

  return (
    <div className="space-y-6" data-testid="stories-page">
      <header>
        <p className="lux-label">Stories</p>
        <h1 className="mt-2 font-serif text-4xl text-lux-text sm:text-5xl">Stories today</h1>
        <p className="mt-3 max-w-2xl text-sm text-lux-muted">
          Share up to {data.limit} discreet moments per day. Each story disappears 24 hours after approval and goes through the same AI + moderator check as photos.
        </p>
      </header>

      <section className="lux-card flex flex-col items-start gap-4 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6" data-testid="stories-upload-card">
        <div>
          <p className="font-serif text-xl text-lux-text">Posted today</p>
          <p className="mt-1 text-sm text-lux-muted" data-testid="stories-today-count">{data.today} of {data.limit} used</p>
        </div>
        <label className="lux-btn lux-btn-primary cursor-pointer" data-testid="stories-upload-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
          {busy ? "Uploading..." : "Add a story"}
          <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="sr-only" onChange={upload} data-testid="stories-upload-input" />
        </label>
      </section>
      {error && <p className="text-sm text-lux-error" role="alert" data-testid="stories-upload-error">{error}</p>}

      {data.stories.length === 0 ? (
        <section className="lux-card flex flex-col items-center rounded-[12px] px-6 py-12 text-center" data-testid="stories-empty">
          <Flame className="h-8 w-8 text-lux-silver" aria-hidden="true" />
          <p className="mt-4 font-serif text-xl text-lux-text">No stories yet</p>
          <p className="mt-1 max-w-sm text-sm text-lux-muted">Stories appear on the city and area pages for 24 hours after approval.</p>
        </section>
      ) : (
        <section className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" aria-label="Your stories">
          {data.stories.map((s) => {
            const [label, cls] = STATE_LABEL[s.status] || [s.status, "text-lux-muted border-lux-border"];
            return (
              <article key={s.id} className="relative overflow-hidden rounded-[12px] border border-lux-border bg-lux-surface2" data-testid="stories-tile">
                <div className="aspect-[3/4]">
                  <img src={media(s.url)} alt="Story" className="h-full w-full object-cover" />
                </div>
                <span className={`absolute left-2 top-2 inline-flex items-center rounded-full border bg-black/70 px-2 py-0.5 text-[10px] font-medium uppercase ${cls}`}>{label}</span>
                <button onClick={() => remove(s.id)}
                  className="absolute right-2 top-2 rounded-full bg-black/70 p-1.5 text-lux-silver hover:text-lux-silverBright"
                  aria-label="Remove story" data-testid="stories-delete">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-3 text-xs text-lux-text">
                  <p>Posted {timeAgo(s.created_at)}</p>
                  {s.status === "approved" && s.expires_at && <p className="text-lux-silver">Expires {timeAgo(s.expires_at)}</p>}
                  {s.status === "rejected" && s.reject_reason && <p className="text-lux-error">{s.reject_reason}</p>}
                  {s.status === "approved" && <p className="text-lux-silver">{s.views} views</p>}
                </div>
              </article>
            );
          })}
        </section>
      )}
    </div>
  );
}
